<template>
  <div class="actividades-lista">
    <!-- Header -->
    <div class="content-header">
      <div class="content-header-left">
        <h1 class="content-header-title">Lista de Actividades</h1>
        <p class="content-header-subtitle">Gestiona todas las actividades del sistema</p>
      </div>
      <div class="content-header-right">
        <b-button variant="primary" @click="nuevaActividad">
          <feather-icon icon="PlusIcon" size="16" class="mr-50" />
          Nueva Actividad
        </b-button>
      </div>
    </div>

    <!-- Filtros -->
    <b-card class="mb-3">
      <b-row>
        <b-col md="3">
          <b-form-group label="Buscar">
            <b-input-group>
              <b-form-input
                v-model="filtros.busqueda"
                placeholder="Buscar actividades..."
                @input="filtrarActividades"
              />
              <b-input-group-append>
                <b-button variant="outline-secondary">
                  <feather-icon icon="SearchIcon" size="16" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Estado">
            <b-form-select
              v-model="filtros.estado"
              :options="opcionesEstado"
              @change="filtrarActividades"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Proyecto">
            <b-form-select
              v-model="filtros.proyecto"
              :options="opcionesProyecto"
              @change="filtrarActividades"
            />
          </b-form-group>
        </b-col>
        <b-col md="3" class="d-flex align-items-end">
          <b-button variant="outline-secondary" @click="limpiarFiltros">
            Limpiar
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Tabla de Actividades -->
    <b-card>
      <b-table
        :items="actividadesFiltradas"
        :fields="actividadesFields"
        responsive
        striped
        hover
        :busy="cargando"
        show-empty
        empty-text="No se encontraron actividades"
        sort-by="proximaAccionFecha"
        :sort-desc="true"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong> Cargando...</strong>
          </div>
        </template>

        <template #cell(estado)="data">
          <b-badge
            :variant="getEstadoVariant(data.item.estado)"
            class="text-capitalize"
          >
            {{ data.item.estado }}
          </b-badge>
        </template>

        <template #cell(prioridad)="data">
          <b-badge
            :variant="getPrioridadVariant(data.item.prioridad)"
            class="text-capitalize"
          >
            {{ data.item.prioridad }}
          </b-badge>
        </template>

        <template #cell(fecha)="data">
          <span v-if="data.item.fecha">
            {{ formatearFechaDDMMYYYY(data.item.fecha) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(fechaVencimiento)="data">
          <span :class="getFechaClass(data.item.fechaVencimiento)">
            {{ formatearFecha(data.item.fechaVencimiento) }}
          </span>
        </template>

        <template #cell(proximaAccionFecha)="data">
          <span v-if="data.item.proximaAccionFecha">
            {{ formatearFechaDDMMYYYY(data.item.proximaAccionFecha) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(nombreProyecto)="data">
          <span 
            v-b-tooltip.hover 
            :title="data.item.nombreProyecto"
            class="text-truncate d-inline-block"
            style="max-width: 100%;"
          >
            {{ data.item.nombreProyecto }}
          </span>
        </template>

        <template #cell(creadoPor.nombre)="data">
          <span 
            v-b-tooltip.hover 
            :title="data.item.creadoPor?.nombre"
            class="text-truncate d-inline-block"
            style="max-width: 100%;"
          >
            {{ data.item.creadoPor?.nombre }}
          </span>
        </template>

        <template #cell(acciones)="data">
          <div class="actions-container">
            <b-button
              variant="outline-primary"
              size="sm"
              @click="verActividad(data.item)"
              v-b-tooltip.hover
              title="Ver actividad"
            >
              <feather-icon icon="EyeIcon" size="12" />
            </b-button>
            <b-button
              variant="outline-warning"
              size="sm"
              @click="editarActividad(data.item)"
              v-b-tooltip.hover
              title="Editar actividad"
            >
              <feather-icon icon="EditIcon" size="12" />
            </b-button>
            <b-button
              variant="outline-info"
              size="sm"
              @click="agregarRecordatorio(data.item)"
              v-b-tooltip.hover
              title="Recordatorio"
            >
              <feather-icon icon="CalendarIcon" size="12" />
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              @click="eliminarActividad(data.item)"
              v-b-tooltip.hover
              title="Eliminar actividad"
            >
              <feather-icon icon="TrashIcon" size="12" />
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- Paginación -->
      <div class="d-flex justify-content-between align-items-center mt-3">
        <div>
          <span class="text-muted">
            Mostrando {{ actividadesFiltradas.length }} de {{ actividades.length }} actividades
          </span>
        </div>
        <b-pagination
          v-model="paginacion.paginaActual"
          :total-rows="paginacion.totalFilas"
          :per-page="paginacion.filasPorPagina"
          @change="cambiarPagina"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import { ActividadService } from '@/services/actividadService'
import { Actividad } from '@/types'
import { 
  generarEnlaceGoogleCalendar, 
  generarEnlaceOutlookCalendar, 
  generarEnlaceYahooCalendar, 
  descargarArchivoICS,
  crearEventoDesdeActividad 
} from '@/utils/calendarUtils'

export default {
  name: 'ActividadesLista',
  data() {
    return {
      cargando: false,
      filtros: {
        busqueda: '',
        estado: '',
        proyecto: '',
      },
      opcionesEstado: [
        { value: '', text: 'Todos los estados' },
        { value: 'Pendiente', text: 'Pendiente' },
        { value: 'En Progreso', text: 'En Progreso' },
        { value: 'Completada', text: 'Completada' },
        { value: 'Cancelada', text: 'Cancelada' },
      ],
      opcionesProyecto: [
        { value: '', text: 'Todos los proyectos' },
        { value: 1, text: 'Proyecto Alpha' },
        { value: 2, text: 'Proyecto Beta' },
        { value: 3, text: 'Proyecto Gamma' },
      ],
      actividadesFields: [
        { key: 'tipoActividad.nombre', label: 'Tipo', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'fecha', label: 'Fecha', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'estadoActividad.nombre', label: 'Estado', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'nombreProyecto', label: 'Proyecto', sortable: true, thClass: 'text-center', tdClass: 'text-truncate' },
        { key: 'creadoPor.nombre', label: 'Creado por', sortable: true, thClass: 'text-center', tdClass: 'text-truncate' },
        { key: 'proximaAccionFecha', label: 'Próx. Acción', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'acciones', label: 'Acciones', thClass: 'text-center', tdClass: 'text-center' },
      ],
      actividades: [],
      paginacion: {
        paginaActual: 1,
        filasPorPagina: 10,
        totalFilas: 0,
      },
    }
  },
  async mounted() {
    await this.cargarActividades()
  },
  computed: {
    actividadesFiltradas() {
      let actividades = this.actividades

      if (this.filtros.busqueda) {
        const busqueda = this.filtros.busqueda.toLowerCase()
        actividades = actividades.filter(actividad =>
          actividad.tipoActividad?.nombre?.toLowerCase().includes(busqueda) ||
          actividad.nombreProyecto?.toLowerCase().includes(busqueda) ||
          actividad.creadoPor?.nombre?.toLowerCase().includes(busqueda) ||
          (actividad.observaciones && actividad.observaciones.toLowerCase().includes(busqueda))
        )
      }

      if (this.filtros.estado) {
        actividades = actividades.filter(actividad => actividad.estadoActividad?.nombre === this.filtros.estado)
      }

      if (this.filtros.proyecto) {
        actividades = actividades.filter(actividad => actividad.idProyecto === this.filtros.proyecto)
      }

      // Ordenar por fecha de próxima acción en orden descendente
      actividades = actividades.sort((a, b) => {
        const fechaA = a.proximaAccionFecha ? new Date(a.proximaAccionFecha).getTime() : 0
        const fechaB = b.proximaAccionFecha ? new Date(b.proximaAccionFecha).getTime() : 0
        
        // Si ambas tienen fecha, ordenar descendente (más reciente primero)
        if (fechaA && fechaB) {
          return fechaB - fechaA
        }
        // Si solo una tiene fecha, la que tiene fecha va primero
        if (fechaA && !fechaB) return -1
        if (!fechaA && fechaB) return 1
        // Si ninguna tiene fecha, mantener el orden original
        return 0
      })

      this.paginacion.totalFilas = actividades.length
      return actividades
    },
  },
  methods: {
    async cargarActividades() {
      this.cargando = true
      try {
        this.actividades = await ActividadService.getAll()
        this.paginacion.totalFilas = this.actividades.length
      } catch (error) {
        console.error('Error al cargar actividades:', error)
        this.$toast({
          component: {
            template: '<div>Error al cargar las actividades</div>',
          },
          props: {
            variant: 'danger',
          },
        })
      } finally {
        this.cargando = false
      }
    },
    filtrarActividades() {
      // Los filtros se aplican automáticamente por el computed
    },
    limpiarFiltros() {
      this.filtros = {
        busqueda: '',
        estado: '',
        proyecto: '',
      }
    },
    getEstadoVariant(estado) {
      const variants = {
        'En Progreso': 'primary',
        'Completada': 'success',
        'Pendiente': 'warning',
        'Cancelada': 'danger',
      }
      return variants[estado] || 'secondary'
    },
    getFechaClass(fecha) {
      const hoy = new Date()
      const fechaVencimiento = new Date(fecha)
      const diffDias = Math.ceil((fechaVencimiento - hoy) / (1000 * 60 * 60 * 24))
      
      if (diffDias < 0) return 'text-danger'
      if (diffDias <= 3) return 'text-warning'
      return 'text-muted'
    },
    formatearFecha(fecha) {
      return new Date(fecha).toLocaleDateString('es-ES')
    },
    formatearFechaDDMMYYYY(fecha) {
      if (!fecha) return '-'
      const date = new Date(fecha)
      const dia = String(date.getDate()).padStart(2, '0')
      const mes = String(date.getMonth() + 1).padStart(2, '0')
      const año = date.getFullYear()
      return `${dia}-${mes}-${año}`
    },
    verActividad(actividad) {
      this.$router.push({ name: 'actividades-detalle', params: { id: actividad.idActividad } })
    },
    editarActividad(actividad) {
      this.$router.push({ name: 'actividades-editar', params: { id: actividad.idActividad } })
    },
    async eliminarActividad(actividad) {
      this.$swal({
        title: '¿Estás seguro?',
        text: 'Esta actividad será eliminada permanentemente',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          try {
            await ActividadService.delete(actividad.idActividad)
            // Recargar la lista de actividades
            await this.cargarActividades()
            this.$toast({
              component: {
                template: '<div>Actividad eliminada correctamente</div>',
              },
              props: {
                variant: 'success',
              },
            })
          } catch (error) {
            console.error('Error al eliminar actividad:', error)
            this.$toast({
              component: {
                template: '<div>Error al eliminar la actividad</div>',
              },
              props: {
                variant: 'danger',
              },
            })
          }
        }
      })
    },
    nuevaActividad() {
      this.$router.push({ name: 'actividades-nueva' })
    },
    cambiarPagina(pagina) {
      this.paginacion.paginaActual = pagina
    },
    async agregarRecordatorio(actividad) {
      // Primero pedir la hora del recordatorio
      const result = await this.$swal({
        title: 'Configurar Recordatorio',
        html: `
          <div class="text-left">
            <p>Selecciona la hora para el recordatorio:</p>
            <div class="form-group">
              <label for="hora">Hora:</label>
              <input type="time" id="hora" class="form-control" value="09:00" required>
            </div>
            <p class="text-muted small mt-2">Fecha: ${this.formatearFecha(actividad.proximaAccionFecha || actividad.fecha)}</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Continuar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
        didOpen: () => {
          // Asegurar que el input esté enfocado
          setTimeout(() => {
            const input = document.getElementById('hora')
            if (input) input.focus()
          }, 100)
        },
        preConfirm: () => {
          const horaInput = document.getElementById('hora')
          if (!horaInput || !horaInput.value) {
            this.$swal.showValidationMessage('Por favor selecciona una hora')
            return false
          }
          return horaInput.value
        }
      })

      if (!result.value) return
      
      const horaRecordatorio = result.value

      // Crear el evento con la hora seleccionada
      const evento = crearEventoDesdeActividad(actividad, horaRecordatorio)
      
      this.$swal({
        title: 'Agregar Recordatorio',
        text: 'Selecciona cómo quieres agregar el recordatorio:',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Google Calendar',
        cancelButtonText: 'Cancelar',
        showDenyButton: true,
        denyButtonText: 'Outlook',
        showCloseButton: true,
        customClass: {
          confirmButton: 'btn btn-success',
          denyButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          // Google Calendar
          const url = generarEnlaceGoogleCalendar(evento)
          window.open(url, '_blank')
        } else if (result.isDenied) {
          // Outlook
          const url = generarEnlaceOutlookCalendar(evento)
          window.open(url, '_blank')
        }
      })
      
      // Mostrar opciones adicionales
      this.$swal({
        title: 'Otras opciones',
        text: 'También puedes descargar el archivo o enviarlo por correo:',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Descargar archivo',
        cancelButtonText: 'Cerrar',
        showDenyButton: true,
        denyButtonText: 'Enviar por correo',
        customClass: {
          confirmButton: 'btn btn-info',
          denyButton: 'btn btn-warning',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          descargarArchivoICS(evento)
        } else if (result.isDenied) {
          this.enviarRecordatorioPorCorreo(actividad, horaRecordatorio)
        }
      })
    },
    async enviarRecordatorioPorCorreo(actividad, horaRecordatorio) {
      const { value: emails } = await this.$swal({
        title: 'Enviar Recordatorio por Correo',
        html: `
          <div class="text-left">
            <p>Ingresa las direcciones de correo electrónico:</p>
            <div class="form-group">
              <label for="emails">Correos electrónicos:</label>
              <textarea 
                id="emails" 
                class="form-control" 
                rows="3" 
                placeholder="ejemplo1@correo.com, ejemplo2@correo.com, ejemplo3@correo.com"
                required
              ></textarea>
              <small class="form-text text-muted">Separa múltiples correos con comas</small>
            </div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Enviar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-warning',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
        preConfirm: () => {
          const emailsInput = document.getElementById('emails')
          if (!emailsInput || !emailsInput.value.trim()) {
            this.$swal.showValidationMessage('Debes ingresar al menos una dirección de correo')
            return false
          }
          
          const emails = emailsInput.value.split(',').map(email => email.trim()).filter(email => email)
          if (emails.length === 0) {
            this.$swal.showValidationMessage('Debes ingresar al menos una dirección de correo válida')
            return false
          }
          
          // Validar cada email
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          const emailsInvalidos = emails.filter(email => !emailRegex.test(email))
          if (emailsInvalidos.length > 0) {
            this.$swal.showValidationMessage(`Correos inválidos: ${emailsInvalidos.join(', ')}`)
            return false
          }
          
          return emails
        }
      })

      if (emails && emails.length > 0) {
        try {
          this.$swal({
            title: 'Enviando...',
            text: `Enviando recordatorio a ${emails.length} destinatario${emails.length > 1 ? 's' : ''}...`,
            icon: 'info',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              this.$swal.showLoading()
            }
          })

          // Generar el evento y el archivo ICS con la hora seleccionada
          const evento = crearEventoDesdeActividad(actividad, horaRecordatorio)
          const archivoICS = generarArchivoICS(evento)
          
          // Enviar el archivo ICS por correo a múltiples destinatarios
          await this.enviarCorreoRecordatorio(actividad.idActividad, emails, archivoICS, evento)
          
          this.$swal({
            title: '¡Enviado!',
            text: `El recordatorio ha sido enviado a ${emails.length} destinatario${emails.length > 1 ? 's' : ''}`,
            icon: 'success',
            confirmButtonText: 'Aceptar',
            customClass: {
              confirmButton: 'btn btn-success',
            },
            buttonsStyling: false,
          })
        } catch (error) {
          console.error('Error al enviar el correo:', error)
          this.$swal({
            title: 'Error',
            text: 'No se pudo enviar el recordatorio. Inténtalo de nuevo.',
            icon: 'error',
            confirmButtonText: 'Aceptar',
            customClass: {
              confirmButton: 'btn btn-danger',
            },
            buttonsStyling: false,
          })
        }
      }
    },
    async enviarCorreoRecordatorio(actividadId, emails, archivoICS, evento) {
      try {
        const response = await this.$http.post('/email/recordatorio-actividad', {
          actividadId: actividadId,
          emailsDestino: emails, // Cambiar a array de emails
          archivoICS: archivoICS,
          evento: evento
        })
        
        if (response.data.success) {
          return Promise.resolve()
        } else {
          throw new Error(response.data.message || 'Error al enviar el correo')
        }
      } catch (error) {
        console.error('Error al enviar correo:', error)
        throw new Error('Error al enviar el recordatorio por correo')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.actividades-lista {
  padding: 1.5rem;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.content-header-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

/* Estilos para los botones de acciones */
.actions-container {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  align-items: center;
  justify-content: flex-start;
  min-width: 200px;
}

.actions-container .btn {
  padding: 0.25rem 0.4rem;
  font-size: 0.75rem;
  line-height: 1;
  border-radius: 0.25rem;
  flex-shrink: 0;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}


.actions-container .btn .feather {
  width: 12px;
  height: 12px;
}

/* Controlar el ancho de las columnas */
.table th:nth-child(1), .table td:nth-child(1) { width: 120px; min-width: 120px; } /* Tipo */
.table th:nth-child(2), .table td:nth-child(2) { width: 100px; min-width: 100px; } /* Fecha */
.table th:nth-child(3), .table td:nth-child(3) { width: 100px; min-width: 100px; } /* Estado */
.table th:nth-child(4), .table td:nth-child(4) { width: 150px; min-width: 150px; } /* Proyecto */
.table th:nth-child(5), .table td:nth-child(5) { width: 120px; min-width: 120px; } /* Creado por */
.table th:nth-child(6), .table td:nth-child(6) { width: 100px; min-width: 100px; } /* Próx. Acción */
.table th:nth-child(7), .table td:nth-child(7) { width: 200px; min-width: 200px; } /* Acciones */

/* Asegurar que la columna de acciones tenga suficiente espacio */
.table td:last-child {
  white-space: nowrap;
  vertical-align: middle;
}

/* Truncamiento de texto para columnas largas */
.text-truncate {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Hover effects mejorados */
.actions-container .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.actions-container .btn:active {
  transform: translateY(0);
}

@media (max-width: 1200px) {
  .actions-container {
    min-width: 180px;
  }
  
  .table td:last-child {
    width: 200px;
    min-width: 200px;
  }
  
  /* Reducir anchos en pantallas medianas */
  .table th:nth-child(4), .table td:nth-child(4) { width: 120px; min-width: 120px; } /* Proyecto */
  .table th:nth-child(5), .table td:nth-child(5) { width: 100px; min-width: 100px; } /* Creado por */
}

@media (max-width: 768px) {
  .content-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .content-header-right {
    margin-top: 1rem;
  }
  
  /* En móviles, hacer que los botones se apilen en dos filas */
  .actions-container {
    flex-direction: column;
    gap: 4px;
    min-width: 80px;
  }
  
  .actions-container .btn {
    width: 100%;
    min-width: 60px;
  }
  
  .table td:last-child {
    width: 100px;
    min-width: 100px;
  }
}
</style>
